"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useHVACAggregation = void 0;
const react_1 = require("react");
const useAlertChangeRefresh_1 = require("./useAlertChangeRefresh");
const queries_1 = require("../../graphql/queries");
const hvac_1 = require("../../utils/aggregation/hvac");
const alertStatus_1 = require("../../utils/alertStatus");
const graphqlCache_1 = require("../../utils/graphqlCache");
const eerDisplayMode_1 = require("../../utils/hvac/eerDisplayMode");
const stableValue_1 = require("../../utils/stableValue");
const useHVACSensorsByGroup_1 = require("../hvac/useHVACSensorsByGroup");
const POLLING_INTERVAL_MS = 30_000;
const useHVACAggregation = (hubId, 
/** Household sensor list — include linked gateways and skip a duplicate fetch. */
allSensors) => {
    const { hvacSensorGroups, loading: groupsLoading } = (0, useHVACSensorsByGroup_1.useHVACSensorsByGroup)(hubId, allSensors);
    const [seerResults, setSeerResults] = (0, react_1.useState)(new Map());
    const [alertResults, setAlertResults] = (0, react_1.useState)(new Map());
    const [filterResults, setFilterResults] = (0, react_1.useState)(new Map());
    const [historicalResults, setHistoricalResults] = (0, react_1.useState)(new Map());
    const [loading, setLoading] = (0, react_1.useState)(true);
    const lastFetchTime = (0, react_1.useRef)(0);
    const intervalRef = (0, react_1.useRef)(null);
    // Keyed by hub AND group: a group that moves between hubs (or arrives from a
    // newly linked gateway) must re-fire its SEER fetch, not reuse the old hub's.
    const groupIds = (0, react_1.useMemo)(() => hvacSensorGroups.map((g) => `${g.hubId}:${g.groupId}`).join(','), [hvacSensorGroups]);
    const fetchAllSeerRatings = (0, react_1.useCallback)(async (force = false) => {
        if (!hubId || hvacSensorGroups.length === 0) {
            setSeerResults(new Map());
            setAlertResults(new Map());
            setFilterResults(new Map());
            setHistoricalResults(new Map());
            setLoading(false);
            return;
        }
        const now = Date.now();
        if (!force && now - lastFetchTime.current < 5000)
            return;
        lastFetchTime.current = now;
        if (force)
            (0, graphqlCache_1.clearGraphqlCache)();
        try {
            // Fetch live SEER data + active alerts per group
            const results = await Promise.all(hvacSensorGroups.map(async (group) => {
                // Alerts are keyed to the SENSOR, not the hub, so both pair members
                // are queried directly. This is what makes an HVAC_CT_FAULT or an
                // overdue filter reach the dashboard card: without it the card's
                // only inputs are the EER band and the offline sweep, and a system
                // that is merely OFF reads Good no matter how many faults are open.
                const memberIds = [group.indoorSensor?.id, group.outdoorSensor?.id].filter(Boolean);
                const [seerData, alerts, filterStatus] = await Promise.all([
                    (async () => {
                        try {
                            // group.hubId, not the caller's hubId — a group on a linked
                            // gateway is partitioned under the gateway.
                            const response = await (0, graphqlCache_1.cachedGraphql)({
                                query: queries_1.getSeerRating,
                                variables: { hubId: group.hubId || hubId, groupId: group.groupId },
                            });
                            return (response.data?.getSeerRating ?? null);
                        }
                        catch {
                            return null;
                        }
                    })(),
                    (async () => {
                        const perSensor = await Promise.all(memberIds.map(async (sensorId) => {
                            try {
                                const response = await (0, graphqlCache_1.cachedGraphql)({
                                    query: queries_1.getSensorAlertsBySensor,
                                    variables: { sensorId, limit: 20 },
                                });
                                return (response.data?.getSensorAlertsBySensor ?? []);
                            }
                            catch {
                                return [];
                            }
                        }));
                        // Dedupe by id — the same alert row can come back for both
                        // members when a fault is raised against the pair.
                        const seen = new Set();
                        return (0, alertStatus_1.activeAlerts)(perSensor.flat()).filter((a) => {
                            if (seen.has(a.id))
                                return false;
                            seen.add(a.id);
                            return true;
                        });
                    })(),
                    (async () => {
                        // Filter health is measured across the AIR HANDLER's static
                        // pressure, so only the indoor half of the pair has one.
                        const airHandlerId = group.indoorSensor?.id;
                        if (!airHandlerId)
                            return null;
                        try {
                            const response = await (0, graphqlCache_1.cachedGraphql)({
                                query: queries_1.getHVACFilterStatus,
                                variables: { sensorId: airHandlerId },
                            });
                            return (response?.data?.getHVACFilterStatus ?? null);
                        }
                        catch {
                            return null;
                        }
                    })(),
                ]);
                return { groupId: group.groupId, seerData, alerts, filterStatus };
            }));
            const seerMap = new Map();
            const alertMap = new Map();
            const filterMap = new Map();
            results.forEach((r) => {
                seerMap.set(r.groupId, r.seerData);
                alertMap.set(r.groupId, r.alerts);
                filterMap.set(r.groupId, r.filterStatus);
            });
            // Same data, same reference: a poll that changes nothing must not re-render
            // every consumer (see utils/stableValue.ts).
            setSeerResults((prev) => (0, stableValue_1.stable)(prev, seerMap));
            setAlertResults((prev) => (0, stableValue_1.stable)(prev, alertMap));
            setFilterResults((prev) => (0, stableValue_1.stable)(prev, filterMap));
            // Snapshot running groups' hourly EER into historical state.
            // This ensures we have a frozen value when the AC turns off,
            // even if the hourly Lambda hasn't written a good record.
            // Only snapshot when system has reliable CT data (instant mode) —
            // grace/peak/off modes during startup can have garbage values.
            setHistoricalResults((prev) => {
                const next = new Map(prev);
                for (const r of results) {
                    if (r.seerData?.hvacRunning) {
                        const group = hvacSensorGroups.find((g) => g.groupId === r.groupId);
                        const isVarSpeed = group?.outdoorSensor?.equipmentDetails?.isVariableSpeed ?? false;
                        const mode = (0, eerDisplayMode_1.determineEERDisplayMode)(r.seerData?.lastMinuteSeerRating, {
                            isVariableSpeed: isVarSpeed,
                            currentRunningPeriodStart: r.seerData?.currentRunningPeriodStart,
                        });
                        if (mode.mode !== 'instant')
                            continue;
                        const hourlyValue = parseFloat(r.seerData.runningHourSeerRating?.seerValue ?? '0');
                        if (hourlyValue > 0) {
                            next.set(r.groupId, hourlyValue);
                        }
                    }
                }
                return (0, stableValue_1.stable)(prev, next);
            });
            // Also try fetching stored historical from SensorDataComputed for groups
            // that are off and don't have a snapshot yet
            const groupsNeedingHistorical = results.filter((r) => {
                if (r.seerData?.hvacRunning)
                    return false;
                const existing = historicalResults.get(r.groupId);
                if (existing && existing > 0)
                    return false; // already have a snapshot
                return true;
            });
            if (groupsNeedingHistorical.length > 0) {
                const historicalFetches = await Promise.all(groupsNeedingHistorical.map(async (r) => {
                    try {
                        const groupHubId = hvacSensorGroups.find((g) => g.groupId === r.groupId)?.hubId || hubId;
                        const response = await (0, graphqlCache_1.cachedGraphql)({
                            query: queries_1.getLastGoodSeerRating,
                            variables: { hubId: groupHubId, groupId: r.groupId },
                        });
                        const seerValue = response.data?.getLastGoodSeerRating?.seerValue;
                        const parsed = typeof seerValue === 'number' ? seerValue : parseFloat(seerValue ?? '0');
                        return { groupId: r.groupId, historicalEer: isNaN(parsed) ? 0 : parsed };
                    }
                    catch {
                        return { groupId: r.groupId, historicalEer: 0 };
                    }
                }));
                setHistoricalResults((prev) => {
                    const next = new Map(prev);
                    historicalFetches.forEach((h) => {
                        if (h.historicalEer > 0)
                            next.set(h.groupId, h.historicalEer);
                    });
                    return (0, stableValue_1.stable)(prev, next);
                });
            }
        }
        catch {
            // Keep existing results on error
        }
        finally {
            setLoading(false);
        }
    }, [hubId, groupIds]);
    (0, react_1.useEffect)(() => {
        fetchAllSeerRatings();
        intervalRef.current = setInterval(() => fetchAllSeerRatings(), POLLING_INTERVAL_MS);
        return () => {
            if (intervalRef.current)
                clearInterval(intervalRef.current);
        };
    }, [fetchAllSeerRatings]);
    // Clearing an alert must drop the badge immediately, not up to 30s later.
    const refreshOnAlertChange = (0, react_1.useCallback)(() => {
        fetchAllSeerRatings(true);
    }, [fetchAllSeerRatings]);
    (0, useAlertChangeRefresh_1.useAlertChangeRefresh)(refreshOnAlertChange);
    return (0, react_1.useMemo)(() => (0, hvac_1.aggregateHVACGroups)(hvacSensorGroups, seerResults, alertResults, filterResults, historicalResults, loading, groupsLoading), [
        hvacSensorGroups,
        seerResults,
        alertResults,
        filterResults,
        historicalResults,
        loading,
        groupsLoading,
    ]);
};
exports.useHVACAggregation = useHVACAggregation;
